import dayjs from "dayjs";
import Cookies from "js-cookie";
import { find } from "lodash";

export function formatDateOnly(value) {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

/** e.g. 1 Adult / 2 Adults */
export function formatAdultCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? "Adult" : "Adults"}`;
}

/** e.g. 1 Child / 2 Childs — returns null when count is 0 */
export function formatChildCountLabel(count) {
  const n = Number(count) || 0;
  if (n === 0) return null;
  return `${n} ${n === 1 ? "Child" : "Children"}`;
}

/** e.g. "1 Adult", "2 Adults & 1 Child" */
export function formatAdultAndChildLabels(adultCount, childCount, joiner = " & ") {
  const adult = formatAdultCountLabel(adultCount);
  const child = formatChildCountLabel(childCount);
  return child ? `${adult}${joiner}${child}` : adult;
}

/** e.g. 1 Traveler / 2 Travelers */
export function formatTravelerCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? "Traveler" : "Travelers"}`;
}

/** e.g. 1 traveller / 2 travellers (fare subtext) */
export function formatTravellerCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? "Traveler" : "Travelers"}`;
}

export function isCreateBookingSuccess(response) {
  const status = String(response?.status || response?.Status || "").toLowerCase();
  return status === "success" || status === "true";
}

export function normalizeCreateBookingResponse(response) {
  if (!response || !isCreateBookingSuccess(response)) return null;

  const payload =
    response.data && typeof response.data === "object" && !Array.isArray(response.data)
      ? response.data
      : response;

  const paymentLink =
    payload.paymentLink ||
    payload.PaymentLink ||
    response.paymentLink ||
    response.PaymentLink ||
    "";

  const bookingId = payload.bookingId ?? payload.BookingId ?? null;
  const bookingRefId =
    payload.bookingRefId ||
    payload.BookingRefId ||
    payload.referenceNo ||
    payload.referenceNumber ||
    "";

  if (!paymentLink && !bookingId && !bookingRefId) return null;

  return {
    bookingId,
    bookingRefId,
    bookingStatus: payload.bookingStatus ?? payload.BookingStatus ?? "",
    bookingTime: payload.bookingTime ?? payload.BookingTime ?? "",
    paymentGateway: payload.paymentGateway ?? payload.PaymentGateway ?? "",
    paymentLink,
    message: response.message ?? response.Message ?? "",
    status: response.status ?? response.Status ?? "",
  };
}

export function parseNumericId(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && !Number.isNaN(value)) return value;

  try {
    const parsed = JSON.parse(value);
    return Number(parsed) || 0;
  } catch {
    return Number(value) || 0;
  }
}

export function buildModuleRoom({
  optionId = 0,
  adult = 0,
  child = 0,
  infant = 0,
  ages = [],
}) {
  return {
    optionId: Number(optionId) || 0,
    adult: Number(adult) || 0,
    child: Number(child) || 0,
    infant: Number(infant) || 0,
    ages: (ages || []).map((entry) => {
      if (entry && typeof entry === "object") {
        return {
          age: Number(entry.age) || 0,
          type: entry.type || "Child",
        };
      }

      return { age: Number(entry) || 0, type: "Child" };
    }),
  };
}

export function buildModuleDataEntry({
  serviceId = 0,
  transferId = 0,
  startTime = "00:00:00",
  slabId = 0,
  couponCode = "",
  rooms = [],
}) {
  return {
    serviceId: Number(serviceId) || 0,
    transferId: Number(transferId) || 0,
    startTime,
    slabId: Number(slabId) || 0,
    couponCode: couponCode || "",
    rooms: (rooms || []).map((room) => buildModuleRoom(room)),
  };
}

/** Payload for /api/Common/CreateCart and /api/Common/CreateTempBooking */
export function buildCartRequestPayload({
  tempUserId = "",
  token = "",
  module = "",
  travelDate = "",
  moduleEntries = [],
}) {
  const entries = (moduleEntries || []).map((entry) => buildModuleDataEntry(entry));

  return {
    tempUserId: tempUserId || "",
    token: token || "",
    module,
    travelDate: formatDateOnly(travelDate),
    modulData: entries,
    moduleData: entries,
  };
}

export function normalizeCartApiPayload(payload = {}) {
  const entries = payload.moduleData || payload.modulData || [];

  return {
    tempUserId: payload.tempUserId || "",
    token: payload.token || "",
    module: payload.module || "",
    travelDate: formatDateOnly(payload.travelDate),
    modulData: entries,
    moduleData: entries,
  };
}

/** Payload for /api/Common/CreateBooking */
export function buildCreateBookingPayload({
  userId = 0,
  tempUserId = "",
  ipAddress = "",
  taxType = "",
  imagePath = "",
  documentPath = "",
  contactDetail = {},
  addressDetail = {},
  passengers = [],
  bookingDetails = [],
  amount = 0,
  currency = "",
  otherTaxes = "",
  feesOthers = "",
  createPaymentLink = true,
  countries
}) {
  console.log(countries);

  return {
    userId: Number(userId) || 0,
    tempUserId: tempUserId || "",
    ipAddress: ipAddress || "",
    taxType,
    imagePath: imagePath || "",
    documentPath: documentPath || "",
    contactDetail: {
      email: contactDetail.email || "",
      mobileCode: String(
        contactDetail.mobileCode ?? contactDetail.mobileCodeId ?? ""
      ),
      mobileNumber: String(contactDetail.mobileNumber || ""),
    },
    addressDetail: {
      add: addressDetail.add || contactDetail.address || "",
      cityName: addressDetail.cityName || contactDetail.city || "",
      stateId: parseInt(addressDetail.stateId ?? contactDetail.state ?? 0),
      gstNo: addressDetail.gstNo || contactDetail.gstNo || "",
      panNumber: addressDetail.panNumber || "",
      leagalName: addressDetail.leagalName || addressDetail.legalName || "",
    },
    passengers: (passengers || []).map((passenger) => ({
      firstName: passenger.firstName || "",
      lastName: passenger.lastName || "",
      dob: formatDateOnly(passenger.dob),
      gender: passenger.gender || "",
      nationalityId: find(countries, c => c.nationality === passenger.nationality)?.id,
      passportNo: passenger.passportNo || "",
      passportExpiryDate: formatDateOnly(
        passenger.passportExpiryDate || passenger.papassportExpiryDate
      ),
      passportIssuingCountryId:
        Number(passenger.passportIssuingCountryId ?? 0) || 0,
    })),
    bookingDetails: (bookingDetails || []).map((detail) => ({
      couponId: Number(detail.couponId ?? 0) || 0,
      moduleType: detail.moduleType || "",
      serviceId: Number(detail.serviceId ?? 0) || 0,
      serviceName: detail.serviceName || "",
      optionId: Number(detail.optionId ?? 0) || 0,
      optionName: detail.optionName || "",
      optionAdultPrice: Number(detail.optionAdultPrice ?? 0),
      optionChildPrice: Number(detail.optionChildPrice ?? 0),
      finalOptionAdultPrice: Number(
        detail.finalOptionAdultPrice ?? detail.optionAdultPrice ?? 0
      ),
      finalOptionChildPrice: Number(
        detail.finalOptionChildPrice ?? detail.optionChildPrice ?? 0
      ),
      finalOptionPrice: Number(detail.finalOptionPrice ?? 0),
      finalPrice: Number(detail.finalPrice ?? 0),
      noOfAdult: Number(detail.noOfAdult ?? 0),
      noOfChild: Number(detail.noOfChild ?? 0),
      travelDate: formatDateOnly(detail.travelDate),
      cartId: parseNumericId(detail.cartId),
      tempBookingId: Number(detail.tempBookingId ?? 0) || 0,
      tax: Number(detail.tax ?? 0),
      discount: Number(detail.discount ?? 0),
    })),
    amount: Number(amount) || 0,
    currency: currency || "",
    otherTaxes: String(otherTaxes ?? ""),
    feesOthers: String(feesOthers ?? ""),
    ...(createPaymentLink ? { createPaymentLink: true } : {}),
  };
}

const PENDING_BOOKING_ID_COOKIE = "nh_pending_booking_id";

export function persistPendingBookingId(bookingId) {
  if (typeof window === "undefined" || bookingId == null || bookingId === "") {
    return;
  }

  Cookies.set(PENDING_BOOKING_ID_COOKIE, String(bookingId), { expires: 1 / 24 });
}

export function getPendingBookingId() {
  if (typeof window === "undefined") return null;
  return Cookies.get(PENDING_BOOKING_ID_COOKIE) || null;
}

export function clearPendingBookingId() {
  if (typeof window === "undefined") return;
  Cookies.remove(PENDING_BOOKING_ID_COOKIE);
}

export function normalizeVerifyPaymentData(raw) {
  if (!raw || typeof raw !== "object") return null;

  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    const nested = raw.data;
    if (
      nested.bookingDetails ||
      nested.bookingReference ||
      nested.customerDetail ||
      nested.bookingId != null ||
      nested.BookingId != null
    ) {
      return nested;
    }
  }

  return raw;
}

export function resolveInvoiceBookingId({
  data,
  booking,
  query,
  includePending = true,
} = {}) {
  const bookingDetails = Array.isArray(data?.bookingDetails)
    ? data.bookingDetails
    : [];

  const candidates = [
    query?.bookingId,
    query?.BookingId,
    data?.bookingId,
    data?.BookingId,
    data?.bookingID,
    data?.customerDetail?.bookingId,
    data?.customerDetail?.BookingId,
    booking?.bookingId,
    booking?.BookingId,
    booking?.bookingDId,
    booking?.id,
    booking?.Id,
    ...bookingDetails.flatMap((item) => [
      item?.bookingId,
      item?.BookingId,
      item?.bookingDId,
      item?.id,
      item?.Id,
    ]),
  ];

  if (includePending) {
    candidates.push(getPendingBookingId());
  }

  for (const value of candidates) {
    const parsed = parseNumericId(value);
    if (parsed > 0) return parsed;
  }

  return null;
}
